import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FileText, Pencil, RefreshCw, Search } from 'lucide-react';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/shared/store/page';
import { useConsentBodiesList } from './api';

function relativeDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('es-CO', { dateStyle: 'short' });
}

export function ConsentBodiesListPage(): JSX.Element {
  usePageTitle(
    'Consentimientos',
    'Edita el cuerpo clínico de cada consentimiento informado',
  );
  const [query, setQuery] = useState<string>('');
  const { data, isLoading, isFetching, isError, refetch } = useConsentBodiesList();

  const items = data?.items ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return items;
    return items.filter(
      (b) =>
        b.cups.toLowerCase().includes(q) ||
        b.nombre.toLowerCase().includes(q) ||
        b.body.toLowerCase().includes(q),
    );
  }, [items, query]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Catálogo de consentimientos
              </CardTitle>
              <CardDescription>
                {items.length} consentimientos disponibles. Las ediciones aplican al
                próximo PDF generado y se registran en la bitácora.
              </CardDescription>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              Recargar
            </Button>
          </div>
        </CardHeader>
        <div className="px-6 pb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por CUPS, nombre o contenido…"
              className="pl-9"
            />
          </div>
        </div>

        {isError && (
          <div className="mx-6 mb-4 px-4 py-3 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>No fue posible cargar los consentimientos. Intenta recargar.</span>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">CUPS</TableHead>
              <TableHead>Procedimiento</TableHead>
              <TableHead className="w-28 text-right">Caracteres</TableHead>
              <TableHead className="w-36">Última edición</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-text-muted py-8">
                  Cargando…
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-text-muted py-8">
                  {query.trim() === ''
                    ? 'No hay consentimientos en el catálogo.'
                    : 'Sin coincidencias para tu búsqueda.'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((b) => (
                <TableRow key={b.cups}>
                  <TableCell className="font-mono text-xs">{b.cups}</TableCell>
                  <TableCell className="font-medium">{b.nombre}</TableCell>
                  <TableCell className="text-right text-text-muted text-sm">
                    {b.body.length.toLocaleString('es-CO')}
                  </TableCell>
                  <TableCell className="text-text-muted text-sm">
                    {relativeDate(b.updatedAt)}
                    {b.updatedBy !== null && (
                      <span className="text-xs block text-text-muted/80">
                        por {b.updatedBy}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button asChild variant="secondary" size="sm">
                      <Link to={`/admin/consentimientos/${encodeURIComponent(b.cups)}`}>
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
